import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { PaymentOptionSubmittedProperties } from '@iwoca/frontend-tracking-library/lib/ampli';
import { Pill, Radio } from '@iwoca/orion';
import classnames from 'classnames';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import styles from './PaymentDetails.module.css';
import { ReactComponent as Shield } from './Shield.svg';
import {
  useGetSellerByHandle,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import { HookFormInput } from '../../../components/Input/Input';
import { displayCurrency } from '../../../utils/DisplayCurrency';
import logger from '../../../utils/logger';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import {
  EmailFormInput,
  useEmailValidation,
} from '../../components/EmailInput/EmailInput';
import { TEnabledOptions } from '../../hooks/useEnabledTerms';
import { RepaymentSchedule } from '../components/RepaymentSchedule/RepaymentSchedule';
import { useCurrentAmount } from '../hooks/useCurrentAmount';
import { useRepaymentSchedule } from '../hooks/useRepaymentSchedule';
import {
  FormValues,
  TDuration,
  TPricingOption,
  TPricingPromotions,
  TRepresentativeInterest,
} from '../utils/PayLinkLanding.types';

const MAX_AMOUNT_PAY_LATER = 15000;
const MIN_AMOUNT_PAY_LATER = 150;

const PRICING_OPTION_TO_TRACKING = {
  payNow: 'pay_now',
  payLater3: 'pay_in_3',
  payLater12: 'pay_in_12',
} as const satisfies Record<
  TPricingOption,
  PaymentOptionSubmittedProperties['payment_option']
>;

export const PaymentDetails = ({
  sellerName,
  pricingPromotions,
  representativeInterest,
  onSubmit,
  initialValues,
  enabledOptions = ['THREE_MONTHS', 'TWELVE_MONTHS', 'PAY_NOW'],
  payLinkType,
}: {
  sellerName: string;
  pricingPromotions: TPricingPromotions;
  representativeInterest: TRepresentativeInterest;
  onSubmit?: ({
    values,
    stateKey,
  }: {
    values: FormValues;
    stateKey?: string;
  }) => Promise<void>;
  initialValues?: {
    amount?: string;
    reference?: string;
  };
  enabledOptions: TEnabledOptions;
  payLinkType: 'custom' | 'generic' | 'package' | 'order' | 'preview';
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { state: customerState } = useGetStateByStateKey();

  const validateAmount = (amount?: FormValues['amount']) => {
    if (!amount || isNaN(parseFloat(amount))) {
      return 'Please enter an amount';
    } else if (parseFloat(amount) > 250000) {
      return 'Please enter an amount up to £250,000.00';
    } else if (parseFloat(amount) < 1) {
      return 'Please enter an amount from £1.00';
    }

    return undefined;
  };

  const validateReference = (reference?: FormValues['reference']) => {
    if (!reference) {
      return 'Please enter a reference';
    } else if (reference.length > 50) {
      return 'Please enter a reference up to 50 characters';
    }

    return undefined;
  };

  const validateEmail = useEmailValidation();

  const validate = async (values: FormValues) => {
    const errors: { [k: string]: string } = {};

    const referenceError = validateReference(values.reference);
    if (referenceError) errors.reference = referenceError;

    const amountError = validateAmount(values.amount);
    if (amountError) errors.amount = amountError;

    if (values.paymentOption?.includes('payLater')) {
      if (
        parseFloat(values.amount) > MAX_AMOUNT_PAY_LATER ||
        parseFloat(values.amount) < MIN_AMOUNT_PAY_LATER
      ) {
        errors.paymentOption =
          'Sorry! Pay later is only available for amounts from £150.00 up to £15,000.00';
      }

      const { errors: emailErrors } = await validateEmail(values);

      if (emailErrors?.emailAddress) {
        errors.emailAddress = emailErrors.emailAddress!;
      }
    }

    if (Object.keys(errors).length > 0) {
      return {
        values: {},
        errors,
      };
    }

    return {
      values,
      errors: {},
    };
  };

  const initialAmount = initialValues?.amount || '';
  const isInitialAmountValid =
    validateAmount(initialValues?.amount) === undefined;

  const initialReference = initialValues?.reference || '';
  const isInitialReferenceValid =
    validateReference(initialReference) === undefined;

  const initialFormValues: FormValues = {
    reference: isInitialReferenceValid ? initialReference : '',
    amount: isInitialAmountValid ? initialAmount : '',
    paymentOption: null,
    emailAddress: '',
    marketingOptIn: true,
  };

  const methods = useForm({
    defaultValues: initialFormValues,
    resolver: validate,
    mode: 'onChange',
  });

  useEffect(() => {
    // Sync to external state of initialFormValues
    // Allows form to reinitialise on change
    methods.reset(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReference, initialAmount]);

  const onHookFormSubmit = methods.handleSubmit(async (values) => {
    if (!onSubmit) return;

    try {
      await onSubmit({
        values,
        stateKey: customerState?.state_key,
      });

      if (payLinkType !== 'preview') {
        tracking.ampli.paymentOptionSubmitted({
          payment_option: PRICING_OPTION_TO_TRACKING[values.paymentOption!],
          // @ts-expect-error Can also be of type "order", library needs update
          pay_link_type: payLinkType,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setSubmitError(
        'Oops something went wrong. Please try again or give us a call on 0203 778 0549.',
      );
      logger.error(error.message, error);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onHookFormSubmit} noValidate>
        <PaymentDetailsForm
          sellerName={sellerName}
          pricingPromotions={pricingPromotions}
          representativeInterest={representativeInterest}
          enabledOptions={enabledOptions}
          submitError={submitError}
          onOptionChange={(paymentOption) => {
            if (payLinkType !== 'preview') {
              tracking.ampli.paymentOptionSelected({
                payment_option: PRICING_OPTION_TO_TRACKING[paymentOption],
                // @ts-expect-error Can also be of type "order", library needs update
                pay_link_type: payLinkType,
              });
            }
          }}
        />
      </form>
    </FormProvider>
  );
};

const PAYMENT_OPTION_INPUT_ID = 'paymentOption';

const PaymentOptionField = ({
  value,
  label,
  children,
  onChange,
}: {
  value: TPricingOption;
  label: ReactNode;
  children?: ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const id = `${PAYMENT_OPTION_INPUT_ID}-${value}`;
  const { register, watch } = useFormContext();
  const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
  const isChecked = watchPaymentOption === value;

  return (
    <Card
      className={classnames(styles.paymentOption, {
        [styles.paymentOptionSelected]: isChecked,
      })}
    >
      <label className={styles.paymentOptionHeader} htmlFor={id}>
        <Radio
          {...register(PAYMENT_OPTION_INPUT_ID, {
            onChange: (e) => {
              onChange?.(e);
            },
          })}
          id={id}
          checked={isChecked}
          value={value}
          className={styles.paymentOptionsRadio}
        />
        <div className={styles.paymentOptionsLabelWrapper}>
          <label className={styles.paymentOptionsLabel} htmlFor={id}>
            {label}
          </label>
          {value === 'payLater12' && (
            <Pill className={styles.newPill} variant="green">
              NEW
            </Pill>
          )}
        </div>
      </label>
      {children && isChecked && (
        <div className={styles.paymentOptionDescription}>{children}</div>
      )}
    </Card>
  );
};

const PaymentDetailsForm = ({
  sellerName,
  pricingPromotions,
  representativeInterest,
  submitError,
  onOptionChange,
  enabledOptions = ['THREE_MONTHS', 'TWELVE_MONTHS', 'PAY_NOW'],
}: {
  sellerName: string;
  pricingPromotions: TPricingPromotions;
  representativeInterest: TRepresentativeInterest;
  submitError: string | null;
  onOptionChange?: (paymentOption: TPricingOption) => void;
  enabledOptions: TEnabledOptions;
}) => {
  const {
    formState,
    setValue: setFieldValue,
    trigger,
    watch,
  } = useFormContext<FormValues>();
  const initialValues = formState.defaultValues!;
  const paymentOption = watch('paymentOption');

  const { state: customerState } = useGetStateByStateKey();
  const loggedInCustomerEmailAddress =
    customerState?.application?.people?.[0].emails?.[0].email;

  useEffect(() => {
    setFieldValue('emailAddress', loggedInCustomerEmailAddress || '');
  }, [loggedInCustomerEmailAddress, setFieldValue, trigger]);

  return (
    <>
      <div className={styles.paymentDetailsContainer}>
        <div className={styles.paymentDetailsHeader}>
          <div className={styles.shieldContainer}>
            <Shield />
          </div>
          Payment to {sellerName}
        </div>
        <div
          className={classnames(
            styles.paymentDetailsReferenceAmountContainer,
            styles.paymentDetailsLabel,
          )}
        >
          Enter a reference and amount
        </div>
        <div className={styles.paymentDetailsFields}>
          <div className={styles.fieldContainer}>
            <div className={styles.amountFieldContainer}>
              <HookFormInput
                label="Amount"
                type="number"
                min="1"
                step="0.01"
                max="250000"
                name="amount"
                id="paymentDetailsAmount"
                disabled={Boolean(initialValues.amount)}
                iconLeft="pound"
              />
            </div>
          </div>

          <div className={styles.fieldContainer}>
            <HookFormInput
              label="Reference"
              type="text"
              name="reference"
              placeholder="Invoice reference"
              id="paymentDetailsReference"
              disabled={Boolean(initialValues.reference)}
            />
          </div>
        </div>
        <div
          className={classnames(
            styles.paymentDetailsLabel,
            styles.paymentDetailsPaymentOptionContainer,
          )}
        >
          Choose a payment option
        </div>
        <div className={styles.paymentOptionsContainer}>
          {enabledOptions.includes('PAY_NOW') && (
            <PaymentOptionField
              value="payNow"
              label={<PayNowRadioOptionLabel />}
              onChange={(e) => {
                onOptionChange?.(e.target.value as TPricingOption);
              }}
            />
          )}
          {enabledOptions.includes('THREE_MONTHS') && (
            <PayLaterOption
              pricingPromotions={pricingPromotions.THREE_MONTHS}
              representativeInterest={representativeInterest.THREE_MONTHS}
              duration={3}
              onChange={(e) => {
                onOptionChange?.(e.target.value as TPricingOption);
              }}
            />
          )}
          {enabledOptions.includes('TWELVE_MONTHS') && (
            <PayLaterOption
              pricingPromotions={pricingPromotions.TWELVE_MONTHS!}
              representativeInterest={representativeInterest.TWELVE_MONTHS!}
              duration={12}
              onChange={(e) => {
                onOptionChange?.(e.target.value as TPricingOption);
              }}
            />
          )}
        </div>
        {!paymentOption && <NoSelection />}
        {paymentOption === 'payNow' && <PayNowSelected />}
        {paymentOption?.includes('payLater') && (
          <EmailFormInput loggedIn={!!loggedInCustomerEmailAddress} />
        )}
        {submitError && <p className={styles.fieldError}>{submitError}</p>}
        <a
          className={styles.trustpilotLogo}
          href="https://uk.trustpilot.com/review/iwoca.co.uk"
          rel="noopener noreferrer"
          target="_blank"
        >
          Secure payments rated 4.8/5 on Trustpilot
        </a>
      </div>
    </>
  );
};

const PayNowRadioOptionLabel = () => {
  const amount = useCurrentAmount();
  const displayAmount = amount && displayCurrency(parseFloat(amount));

  return (
    <p className={styles.paymentOptionLabel}>
      {amount ? (
        <>
          <b> Pay {displayAmount} </b> with an instant bank transfer
        </>
      ) : (
        <>
          <b> Pay Now </b> with an instant bank transfer
        </>
      )}
    </p>
  );
};

function checkIsEligibleForPayLater(amount: number) {
  if (!amount) return true;

  if (amount >= MIN_AMOUNT_PAY_LATER && amount <= MAX_AMOUNT_PAY_LATER)
    return true;

  return false;
}

const PayLaterOption = ({
  pricingPromotions,
  representativeInterest,
  onChange,
  duration,
}: {
  pricingPromotions: string[];
  representativeInterest: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  duration: TDuration;
}) => {
  const amount = useCurrentAmount();
  const debouncedAmount = useDebounce(amount, 500);

  const { exampleMonthlyRepayment, totalInterest } = useRepaymentSchedule({
    amount: debouncedAmount,
    representativeInterest,
    duration,
    pricingPromotions,
  });

  const isEligibleForPayLater = checkIsEligibleForPayLater(
    parseInt(amount || ''),
  );

  if (!isEligibleForPayLater) {
    return (
      <PaymentOptionField
        value={`payLater${duration}`}
        label={
          <PayLaterRadioOptionLabel
            exampleMonthlyRepayment={exampleMonthlyRepayment}
            representativeInterest={representativeInterest}
            duration={duration}
          />
        }
        onChange={onChange}
      />
    );
  }

  return (
    <PaymentOptionField
      value={`payLater${duration}`}
      label={
        <PayLaterRadioOptionLabel
          exampleMonthlyRepayment={exampleMonthlyRepayment}
          representativeInterest={representativeInterest}
          duration={duration}
        />
      }
      onChange={onChange}
    >
      <PayLaterFieldOption
        totalInterest={totalInterest}
        exampleMonthlyRepayment={exampleMonthlyRepayment}
        representativeInterest={representativeInterest}
        duration={duration}
      />
    </PaymentOptionField>
  );
};

const PayLaterRadioOptionLabel = ({
  exampleMonthlyRepayment,
  representativeInterest,
  duration,
}: {
  exampleMonthlyRepayment?: string;
  representativeInterest: number;
  duration: TDuration;
}) => {
  const amount = useCurrentAmount();
  const isInterestFree = representativeInterest === 0;

  if (!amount || !exampleMonthlyRepayment) {
    if (isInterestFree) {
      return (
        <p className={styles.paymentOptionLabel}>
          <b> Pay Later for {duration} months, </b>
          <i> interest free </i>
        </p>
      );
    }
    return (
      <p className={styles.paymentOptionLabel}>
        <b> Pay Later </b> with {duration} monthly installments
      </p>
    );
  }

  if (parseFloat(amount) < MIN_AMOUNT_PAY_LATER) {
    return (
      <p className={styles.paymentOptionLabel}>
        <b> Pay over {duration} months </b> isn't available for under £150
      </p>
    );
  }

  if (parseFloat(amount) > MAX_AMOUNT_PAY_LATER) {
    return (
      <p className={styles.paymentOptionLabel}>
        <b> Pay over {duration} months </b> isn't available for over £15,000
      </p>
    );
  }

  if (isInterestFree) {
    return (
      <p className={styles.paymentOptionLabel}>
        <b> Pay {exampleMonthlyRepayment} </b> / month for {duration} months,
        <i> interest free </i>
      </p>
    );
  }

  return (
    <p className={styles.paymentOptionLabel}>
      <b> Pay {exampleMonthlyRepayment} </b> / month for {duration} months
    </p>
  );
};

const PayLaterFieldOption = ({
  totalInterest,
  exampleMonthlyRepayment,
  duration,
  representativeInterest,
}: {
  totalInterest?: string;
  exampleMonthlyRepayment?: string;
  duration: TDuration;
  representativeInterest: number;
}) => {
  const amount = useCurrentAmount();
  const { sellerHandle } = useParams();
  const { seller, loadingSeller } = useGetSellerByHandle(sellerHandle);

  if (loadingSeller) return null;

  return (
    <>
      <div className={styles.payLaterDescription}>
        {(!amount || !totalInterest) &&
          `Split the cost into ${duration} automatic monthly payments, while your supplier gets paid today.`}

        {amount &&
          totalInterest === '£0.00' &&
          `Split the cost into ${duration} automatic monthly payments, while your supplier gets paid today.`}

        {amount &&
          totalInterest !== '£0.00' &&
          `Including ${totalInterest} interest for the full ${
            duration === 3 ? 90 : 365
          } days. Rates vary based on your business (${parseFloat(
            (representativeInterest * 100).toFixed(2),
          )}% monthly rep rate)`}
      </div>

      <RepaymentSchedule
        exampleMonthlyRepayment={exampleMonthlyRepayment}
        duration={duration}
      />

      {seller!.capture_delay_hours ? (
        <div className={styles.capturedPaymentDescription}>
          <p className={styles.capturedPaymentSubheading}>
            Your payment schedule will begin once {seller!.seller_name} has
            confirmed the order.{' '}
            <span className={styles.capturedPaymentSubheadingBold}>
              Your first payment will be 30 days after that date.
            </span>
          </p>
        </div>
      ) : null}
    </>
  );
};

const NoSelection = () => (
  <div>
    <Button type="submit" className={styles.secureCheckoutButton} disabled>
      Finish checkout
    </Button>
  </div>
);

const PayNowSelected = () => {
  const { formState } = useFormContext();
  const errors = formState.errors;
  const isSubmitting = formState.isSubmitting;

  const isSubmitDisabled = isSubmitting || Object.keys(errors).length !== 0;

  return (
    <div>
      <Button
        type="submit"
        className={styles.secureCheckoutButton}
        disabled={isSubmitDisabled}
        loading={isSubmitting}
      >
        Finish checkout
      </Button>
    </div>
  );
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!isMountedRef) return;
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
